import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  Input,
  Button,
  Card,
  Typography,
  Space,
  Row,
  Col,
  Select,
  Slider,
  message,
  Spin,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import LoadingSpinner from "../common/LoadingSpinner";
import ErrorMessage from "../common/ErrorMessage";

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Option } = Select;

function ImageGenerator() {
  const [prompt, setPrompt] = useState("");
  const [quality, setQuality] = useState("high");
  const [n, setN] = useState(1);
  const [width, setWidth] = useState(1024);
  const [height, setHeight] = useState(1024);
  const [imageUrls, setImageUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { logout } = useAuth();

  const generateImage = async () => {
    if (!prompt.trim()) {
      setError("Please enter a prompt before generating images");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");

      if (!token) {
        setError("Session expired. Please log in again.");
        logout();
        return;
      }

      const apiUrl = process.env.REACT_APP_BACKEND_URL || "";

      // Ensure we're using the correct parameter values that the backend expects
      const qualityParam =
        quality === "high"
          ? "high"
          : quality === "medium"
          ? "medium"
          : quality === "low"
          ? "low"
          : "auto";
      const nParam = Math.max(1, Math.min(4, n)); // Ensure n is between 1-4
      const widthParam = Math.max(256, Math.min(1536, width)); // Ensure width is valid
      const heightParam = Math.max(256, Math.min(1536, height)); // Ensure height is valid

      // Debug: Log the request details
      console.log("Image generation request details:");
      console.log("API URL:", `${apiUrl}/api/generate-image`);
      console.log("Prompt:", prompt);
      console.log("Quality:", qualityParam);
      console.log("N:", nParam);
      console.log("Width:", widthParam);
      console.log("Height:", heightParam);
      console.log(
        "Full URL:",
        `${apiUrl}/api/generate-image?prompt=${encodeURIComponent(
          prompt
        )}&quality=${qualityParam}&n=${nParam}&width=${widthParam}&height=${heightParam}`
      );

      const response = await fetch(
        `${apiUrl}/api/generate-image?prompt=${encodeURIComponent(
          prompt
        )}&quality=${qualityParam}&n=${nParam}&width=${widthParam}&height=${heightParam}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Response status:", response.status);
      console.log("Response headers:", response.headers);

      if (response.status === 401 || response.status === 403) {
        setError("Your session has expired. Please log in again.");
        logout();
        return;
      }

      if (!response.ok) {
        let errorMessage = `HTTP error! Status: ${response.status}`;
        try {
          const errorData = await response.json();
          console.log("Error response data:", errorData);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (jsonError) {
          console.log("Failed to parse error response as JSON:", jsonError);
          try {
            const errorText = await response.text();
            console.log("Error response text:", errorText);
            if (errorText) errorMessage = errorText;
          } catch (textError) {
            console.log("Failed to read error response text:", textError);
          }
        }
        throw new Error(errorMessage);
      }

      const urls = await response.json();
      console.log("Generated image URLs:", urls);
      setImageUrls(urls);
      message.success(`Successfully generated ${urls.length} image(s)!`);
    } catch (error) {
      console.error("Error generating image:", error);
      setError(error.message || "An error occurred while generating images");
    } finally {
      setLoading(false);
    }
  };

  const clearImages = () => {
    setImageUrls([]);
    setError(null);
  };

  return (
    <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "20px" }}>
      <Title
        level={2}
        style={{ textAlign: "center", marginBottom: "24px", color: "#1890ff" }}
      >
        🎨 Generate Images
      </Title>

      {error && (
        <ErrorMessage
          message="Error"
          description={error}
          type="error"
          closable={true}
        />
      )}

      <Card title="Image Generation Settings" style={{ marginBottom: "24px" }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <div>
            <Text strong style={{ display: "block", marginBottom: "8px" }}>
              Prompt
            </Text>
            <TextArea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe what you want to generate (e.g., 'a futuristic city at sunset')"
              rows={3}
              style={{ borderRadius: "8px" }}
            />
          </div>

          <Row gutter={16}>
            <Col span={8}>
              <Text strong style={{ display: "block", marginBottom: "8px" }}>
                Quality
              </Text>
              <Select
                value={quality}
                onChange={setQuality}
                style={{ width: "100%" }}
              >
                <Option value="high">High Definition</Option>
                <Option value="medium">Standard</Option>
                <Option value="low">Low</Option>
                <Option value="auto">Auto</Option>
              </Select>
            </Col>
            <Col span={8}>
              <Text strong style={{ display: "block", marginBottom: "8px" }}>
                Number of Images
              </Text>
              <Slider
                min={1}
                max={4}
                value={n}
                onChange={setN}
                marks={{ 1: "1", 2: "2", 3: "3", 4: "4" }}
                tooltip={{ formatter: (value) => `${value} images` }}
              />
            </Col>
            <Col span={8}>
              <Text strong style={{ display: "block", marginBottom: "8px" }}>
                Image Dimensions
              </Text>
              <div style={{ display: "flex", gap: "16px" }}>
                <div style={{ flex: 1 }}>
                  <Text style={{ fontSize: "12px", color: "#666" }}>Width: {width}px</Text>
                  <Slider
                    min={256}
                    max={1536}
                    step={64}
                    value={width}
                    onChange={setWidth}
                    style={{ marginTop: "4px" }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Text style={{ fontSize: "12px", color: "#666" }}>Height: {height}px</Text>
                  <Slider
                    min={256}
                    max={1536}
                    step={64}
                    value={height}
                    onChange={setHeight}
                    style={{ marginTop: "4px" }}
                  />
                </div>
              </div>
            </Col>
          </Row>

          <Row gutter={16} justify="space-between">
            <Col>
              <Button onClick={clearImages} style={{ marginRight: "8px" }}>
                Clear
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                onClick={generateImage}
                loading={loading}
                disabled={loading || !prompt.trim()}
                style={{
                  backgroundColor: "#1890ff",
                  borderColor: "#1890ff",
                }}
              >
                {loading ? "Generating..." : "Generate Images"}
              </Button>
            </Col>
          </Row>
        </Space>
      </Card>

      {loading && <LoadingSpinner tip="Generating your images..." />}

      {imageUrls.length > 0 && !loading && (
        <Card
          title={`Generated Images (${imageUrls.length})`}
          style={{ marginBottom: "16px" }}
        >
          <Row gutter={[16, 16]}>
            {imageUrls.map((url, index) => (
              <Col xs={24} sm={12} md={8} lg={6} key={index}>
                <Card
                  hoverable
                  style={{ height: "100%" }}
                  cover={
                    <div
                      style={{
                        position: "relative",
                        paddingTop: "100%",
                        backgroundColor: "#f0f0f0",
                      }}
                    >
                      <img
                        src={url}
                        alt={`Generated image ${index + 1}`}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "8px 8px 0 0",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "block";
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          display: "none",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#f0f0f0",
                          color: "#666",
                          borderRadius: "8px 8px 0 0",
                        }}
                      >
                        Failed to load image
                      </div>
                    </div>
                  }
                >
                  <Card.Meta
                    title={`Image ${index + 1}`}
                    description={`Quality: ${
                      quality === "high"
                        ? "High Definition"
                        : quality === "medium"
                        ? "Standard"
                        : quality === "low"
                        ? "Low"
                        : "Auto"
                    } • ${width}x${height}px`}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {!imageUrls.length && !loading && (
        <Card style={{ textAlign: "center", color: "#8c8c8c" }}>
          <Text>
            Enter a prompt above and configure your settings to generate
            AI-powered images.
            <br />
            Try descriptive prompts like "a majestic dragon flying over
            mountains at sunset" for best results!
          </Text>
        </Card>
      )}
    </div>
  );
}

export default ImageGenerator;
